import { ref } from "vue";
import { useToast } from "@chatfood/design-system";
import { isCustomerRepoError } from "@chatfood/dbe-client/errors";
import type { IComposableInterface, IAddress } from "./types";
import { ValidationError } from "./validation.error";
import { useBugReporter } from "#imports";
import { useLocale } from "~/composables/use-locale";

export const useCommon = () => {
  const { toastUnexpectedError } = useToast();
  const { report } = useBugReporter();
  const { t } = useLocale();

  const formErrors = ref<IComposableInterface["formErrors"]["value"]>({});
  const isLoading = ref<IComposableInterface["isLoading"]["value"]>(true);
  const isSaving = ref<IComposableInterface["isLoading"]["value"]>(false);
  const record = ref<IAddress>({
    lat: 0,
    lng: 0,
    nickname: "home",
    location: "",
    city: "",
  });

  const getNicknameLabel = (value: IAddress["nickname"]): string => {
    if (value === "home") {
      return t("components.address_form.address_type.home");
    }

    if (value === "work") {
      return t("components.address_form.address_type.work");
    }

    return t("components.address_form.address_type.other");
  };

  const noop = () => {};

  const setFieldValue = (
    key: keyof IAddress,
    value: IAddress[keyof IAddress],
  ) => {
    record.value = {
      ...record.value,
      [key]: value,
    };
  };

  const handleSaveError = (e: unknown) => {
    if (
      (isCustomerRepoError(e) && e.isValidation() && e.hasMeta()) ||
      e instanceof ValidationError
    ) {
      formErrors.value = {
        location: e.meta.location,
        lat: e.meta.lat,
        lng: e.meta.lng,
        nickname: e.meta.nickname,
        city: e.meta.city,
        number: e.meta.number,
        instruction: e.meta.instruction,
        zipCode: e.meta.zipCode,
      };

      return;
    }

    toastUnexpectedError();
    report(e);
  };

  return {
    getNicknameLabel,
    noop,
    handleSaveError,
    isLoading,
    isSaving,
    formErrors,
    record,
    setFieldValue,
  };
};
